import React, { useEffect } from 'react';
import { Flex, Text, Tabs, TabList, Tab, TabPanel } from '@backstage/ui';
import { Entity } from '@backstage/catalog-model';
import { RiCloseLine } from '@remixicon/react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { useInspectorParams } from '../../hooks';
import { InspectorOverview } from './inspector-overview.component';
import { InspectorJson } from './inspector-json.component';
import { InspectorYaml } from './inspector-yaml.component';
import { onKeyActivate } from '../../../../shared/utils';
import styles from './entity-inspector.module.css';

interface EntityInspectorProps {
  entity: Entity;
}

export const EntityInspector = ({ entity }: EntityInspectorProps) => {
  const { t } = useTranslationRef(appTranslationRef);
  const { selectedTab, close, selectTab } = useInspectorParams();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [close]);

  return (
    <div
      className={styles.overlay}
      onClick={close}
      onKeyDown={onKeyActivate(close)}
      role="button"
      tabIndex={0}
    >
      <div
        className={styles.dialog}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="presentation"
      >
        <Flex justify="between" align="center" className={styles.header}>
          <Text variant="title-small" weight="bold">
            {t('entityInspector.title')}
          </Text>
          <button
            onClick={close}
            className={styles.closeButton}
            aria-label={t('entityInspector.close')}
          >
            <RiCloseLine size={20} />
          </button>
        </Flex>

        <Tabs
          defaultSelectedKey={selectedTab || 'overview'}
          onSelectionChange={key => selectTab(key as string)}
        >
          <TabList>
            <Tab id="overview">{t('entityInspector.tabs.overview')}</Tab>
            <Tab id="json">{t('entityInspector.tabs.json')}</Tab>
            <Tab id="yaml">{t('entityInspector.tabs.yaml')}</Tab>
          </TabList>
          <TabPanel id="overview" className={styles.contentScroll}>
            <InspectorOverview entity={entity} />
          </TabPanel>
          <TabPanel id="json" className={styles.contentScroll}>
            <InspectorJson entity={entity} />
          </TabPanel>
          <TabPanel id="yaml" className={styles.contentScroll}>
            <InspectorYaml entity={entity} />
          </TabPanel>
        </Tabs>
      </div>
    </div>
  );
};
